import { useCallback, useContext, useEffect, useState } from "react";

import { MediaLoadPriorityContext } from "@is/contexts";
import { LoadMediaItem, MediaManager } from "@is/services/MediaManager";

import { THROTTLE_INTERVAL } from "./constants";
import useThrottle from "./useThrottle";

export default function usePreloadMedia(
  loadMediaItems: Array<LoadMediaItem>,
  shouldReportProgress: boolean
) {
  const loadPriority = useContext(MediaLoadPriorityContext);
  const [isLoading, setIsLoading] = useState(false);

  // calling the setProgressPercentage with throttling to decrease the number of renders
  const [progressPercentage, setProgressPercentage] = useThrottle(
    0,
    THROTTLE_INTERVAL
  );

  const progressCallback = useCallback(
    (progress: number) => setProgressPercentage(Math.trunc(progress * 100)),
    [setProgressPercentage]
  );

  const terminateAllDownloads = useCallback(() => {
    MediaManager.instance.terminateAllDownloads();
  }, []);

  useEffect(() => {
    if (loadMediaItems && loadMediaItems.length > 0) {
      const result = MediaManager.instance.preload(
        loadMediaItems,
        loadPriority,
        shouldReportProgress ? progressCallback : undefined
      );

      if (result) {
        setIsLoading(true);

        result.promise
          .then(() => {
            setIsLoading(false);
          })
          .catch((error) => {
            if (
              !(error instanceof DOMException && error.name === "AbortError")
            ) {
              throw error;
            }
          });

        return () => result.cancel();
      }
    }
  }, [loadMediaItems, loadPriority, progressCallback, shouldReportProgress]);

  return { isLoading, progressPercentage, terminateAllDownloads };
}
